import React from 'react';
import styled from '@emotion/styled';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import { FILTER_MOBILE_MAX_WIDTH_PX, filterTokens } from './filterTokens';

const { color } = filterTokens;
/** 폰에서의 칩 높이 — PC 는 filterTokens.chipHeight(36) */
const MOBILE_CHIP_HEIGHT: number = 32;

const ChipButton = styled.button<{ selected: boolean; plain: boolean }>`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 5px;
    height: ${filterTokens.chipHeight}px;
    padding: ${({ plain }) => (plain ? '0 4px 0 8px' : '0 12px 0 14px')};
    border-radius: ${filterTokens.chipHeight / 2}px;
    border: 1px solid ${({ selected, plain }) => (plain ? 'transparent' : selected ? color.accent : color.border)};
    background: ${({ selected, plain }) => (plain ? 'transparent' : selected ? color.selectedBg : color.surface)};
    color: ${({ selected, plain }) => (plain ? color.textSub : selected ? color.selectedText : color.text)};
    font-family: inherit;
    font-size: 14px;
    font-weight: ${({ selected }) => (selected ? 700 : 500)};
    line-height: 1;
    white-space: nowrap;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;

    &:focus-visible {
        outline: 2px solid ${color.accent};
        outline-offset: 2px;
    }

    svg {
        font-size: 18px;
    }

    /* 폰: 머리(탭 + 칩 줄)가 화면을 많이 차지해 칩을 한 단계 작게(높이 32, 글자 13) — 2026-10-01 */
    @media (max-width: ${FILTER_MOBILE_MAX_WIDTH_PX}px) {
        gap: 4px;
        height: ${MOBILE_CHIP_HEIGHT}px;
        padding: ${({ plain }) => (plain ? '0 4px 0 8px' : '0 10px 0 12px')};
        border-radius: ${MOBILE_CHIP_HEIGHT / 2}px;
        font-size: 13px;

        svg {
            font-size: 16px;
        }
    }
`;

const Badge = styled.span`
    min-width: 19px;
    padding: 0 6px;
    border-radius: 10px;
    background: ${color.accent};
    color: #fff;
    font-size: 12px;
    font-weight: 700;
    line-height: 19px;
    text-align: center;
    box-sizing: border-box;
`;

export interface FilterChipProps {
    label: string;
    /** 조건이 걸려 있음 — 옅은 청록 바탕·청록 테두리 */
    selected?: boolean;
    /** 누르면 시트·팝업이 열리는 칩 — 오른쪽 화살표, aria-expanded */
    hasPanel?: boolean;
    /** 이 칩의 시트·팝업이 열려 있음 */
    open?: boolean;
    /** 켜고 끄는 칩(예: 무료만) — aria-pressed */
    toggle?: boolean;
    icon?: React.ReactNode;
    /** 0 보다 크면 라벨 옆 숫자 동그라미 */
    badge?: number;
    /** 테두리 없는 글자형(PC 줄 오른쪽 끝 정렬 등) */
    plain?: boolean;
    /** 오른쪽 화살표 — 기본은 hasPanel 과 같다([필터 N]처럼 아이콘이 있는 칩은 끈다) */
    arrow?: boolean;
    ariaLabel?: string;
    /** 칩 버튼 — 주소로 들어오며 창을 바로 열 때(홈 "직무별" 바로가기) 팝업 자리를 잡는 데 쓴다 */
    buttonRef?: React.Ref<HTMLButtonElement>;
    onClick: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

/** 필터 칩 한 개 — PC 높이 36·반경 18, 폰 높이 32·반경 16 */
export const FilterChip = ({ label, selected = false, hasPanel = false, open = false, toggle = false, icon, badge, plain = false, arrow, ariaLabel, buttonRef, onClick }: FilterChipProps) => (
    <ChipButton
        ref={buttonRef}
        type="button"
        selected={selected}
        plain={plain}
        aria-label={ariaLabel}
        aria-haspopup={hasPanel ? 'dialog' : undefined}
        aria-expanded={hasPanel ? open : undefined}
        aria-pressed={toggle ? selected : undefined}
        onClick={onClick}
    >
        {icon}
        {label}
        {badge !== undefined && badge > 0 && <Badge aria-hidden="true">{badge}</Badge>}
        {(arrow ?? hasPanel) && (open ? <KeyboardArrowUpRoundedIcon aria-hidden="true" /> : <KeyboardArrowDownRoundedIcon aria-hidden="true" />)}
    </ChipButton>
);

export interface FilterChipLabelProps {
    label: string;
    /** 화면 낭독기용 설명(예: "지금 보고 있는 공간: 치과위생사") */
    ariaLabel?: string;
}

/**
 * 누르지 않는 표시용 칩 — [FilterChip] 과 같은 모양·크기(PC 36·폰 32, 선택 색)로 지금 상태를 보여 준다.
 * 속닥속닥 목록의 공간 라벨(치과위생사·치과의사) 처럼 고를 수 없는 값을 필터 줄에 둘 때 쓴다. 버튼이 아니라 글자다.
 */
export const FilterChipLabel = ({ label, ariaLabel }: FilterChipLabelProps) => (
    <ChipButton as="span" selected plain={false} aria-label={ariaLabel} style={{ cursor: 'default' }}>
        {label}
    </ChipButton>
);

const Bar = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
`;

const Scroller = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex: 1;
    min-width: 0;
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;

    &::-webkit-scrollbar {
        display: none;
    }
`;

const End = styled.div`
    flex-shrink: 0;
    margin-left: auto;
`;

export interface FilterChipBarProps {
    ariaLabel: string;
    children: React.ReactNode;
    /** 가로 스크롤 밖 오른쪽 끝에 고정할 것(PC 의 정렬 등) */
    end?: React.ReactNode;
    className?: string;
}

/**
 * 칩 한 줄 — 넘치면 가로로 민다.
 * `data-no-swipe-back`: 앱의 가장자리 스와이프 뒤로가기가 칩 줄을 미는 손가락을 가져가지 않게 한다.
 */
export const FilterChipBar = ({ ariaLabel, children, end, className }: FilterChipBarProps) => (
    <Bar className={className} role="toolbar" aria-label={ariaLabel}>
        <Scroller data-no-swipe-back="">{children}</Scroller>
        {end && <End>{end}</End>}
    </Bar>
);
